import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { LoginFormData, LoginValidationErrors } from './Login.types';
import { loginUser, selectLoginState, resetLoginState, fetchCartItems, fetchWithcookieData, selectIsUserLoggedIn, selectIsAdminLoggedIn } from "../../../redux/WithcookieSlice";
import './Login.css';
import { adaptLoginProps } from './Login.adapter';
import { useTranslation } from '../../../redux/TranslationHelper';

/**
 * 로그인 컴포넌트
 * 사용자 로그인 기능을 제공하는 컴포넌트
 */
const LoginComponent: React.FC<any> = (props) => {
  // 다국어 처리를 위한 훅 사용
  const { t } = useTranslation();


  // props의 id를 사용하여 현재 컴포넌트의 실시간 상태를 직접 구독
  const componentId = props.id;
  const allComponents = useSelector((state: any) => state.editor.components);
  const globalComponent = allComponents.find((comp: any) => comp.id === componentId);
  

  // 어댑터를 통해 props 변환
  const adaptedProps = adaptLoginProps(props);
  
  
  const {
    style = {},
    redirectUrl = '/',
    className = '',
    content = {},
    data = {},
    componentProps = {},
    ...rest
  } = adaptedProps;


  // 배경 타입을 직접 처리
  // 1. 컴포넌트를 직접 리덕스 스토어에서 조회
  const globalComponents = useSelector((state: any) => state.editor.components);
  const reduxComponent = globalComponents?.find((comp: any) => comp.id === props.id);
  
  // 2. 배경 타입 결정 (우선순위: 전역 상태 > props)
  const backgroundType = reduxComponent?.componentProps?.backgroundType || 
                        props.componentProps?.backgroundType || 
                        componentProps?.backgroundType || 
                        'image';
  
  // 3. 배경 URL 결정 (우선순위: 전역 상태 > props)
  const backgroundUrl = reduxComponent?.componentProps?.backgroundUrl || 
                       props.componentProps?.backgroundUrl || 
                       componentProps?.backgroundUrl || 
                       '';
  

  // 5. 효과적으로 렌더링하기 위한 최신 상태 확인 (강제 리렌더링 용도)
  const [renderCount, setRenderCount] = useState(0);
  
  // 6. 배경 타입이 변경될 때마다 리렌더링
  useEffect(() => {
    setRenderCount(prev => prev + 1);
  }, [backgroundType, backgroundUrl]);
  
  // Redux 타입 에러 해결을 위해 any 타입 단언 사용
  const dispatch = useDispatch() as any;
  const { loading, loginSuccess, loginError, validationErrors: reduxValidationErrors } = useSelector(selectLoginState);
  const withcookieData = useSelector((state: any) => state.withcookie.withcookieData);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  
  // 테마 색상 설정
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  
  // 디버깅용 useEffect - 스킨 정보가 로드되었는지 확인
  useEffect(() => {
    if (withcookieData?.skin?.theme) {

    } else {
      // 스킨 데이터가 아직 로드되지 않았다면 로드 시도
      dispatch(fetchWithcookieData());
    }
  }, [withcookieData, dispatch, t]);
  
  // 리다이렉트 경로 설정
  const finalRedirectUrl = componentProps?.redirectPath || redirectUrl || '/';

  // 이미 로그인되어 있는 경우 리다이렉트 경로로 바로 이동
  useEffect(() => {
    // 현재 URL 가져오기
    const currentPath = window.location.pathname;
    
    // URL에 "/edit"이 포함된 경우 리다이렉트하지 않음
    if (currentPath.includes("/edit")) {
      return;
    }
    
    if (isUserLoggedIn) {
      //모바일또는 태블릿이고, 24번 컴패니인 경우
      if ((withcookieData?.skin?.company?.id== 24 || withcookieData?.skin?.company?.id== 21 || withcookieData?.skin?.company?.id== 26) && (window.innerWidth < 900 || window.innerWidth < 1200)) {
        window.location.href = '/finapp';
      }else if ((withcookieData?.skin?.company?.id== 27 || withcookieData?.skin?.company?.id== 28) && (window.innerWidth < 900 || window.innerWidth < 1200)) {
        window.location.href = '/gpc';
      }
      else{
        window.location.href = finalRedirectUrl;
      }
    }
  }, [isUserLoggedIn, isAdminLoggedIn, finalRedirectUrl, t]);
  
  // 버튼 색상 설정 (속성 패널에서 설정한 값 또는 componentProps에서 설정한 값 사용)
  const buttonColorValue = componentProps?.buttonColor || (style as any)?.buttonColor;
  // 스킨의 primary 색상을 우선 사용, 없으면 buttonColorValue, 기본값은 "#007bff"
  const primaryColor = colorSet?.primary || buttonColorValue || "#007bff";
  const primaryDarkColor = colorSet?.primaryDark || shadeColor(primaryColor, -15); // 어두운 버전의 주 색상 (호버 효과용)
  const secondaryColor = colorSet?.secondary || "#6c757d";

  // 색상을 어둡게 하는 유틸리티 함수
  function shadeColor(color: string, percent: number): string {
    try {
      // '#RRGGBB' 포맷이 아닌 경우 기본값 처리
      if (!color || !color.startsWith('#') || color.length !== 7) {
        return percent < 0 ? '#005bb9' : '#3399ff'; // 기본 어두운/밝은 색상
      }

      let R = parseInt(color.substring(1, 3), 16);
      let G = parseInt(color.substring(3, 5), 16);
      let B = parseInt(color.substring(5, 7), 16);

      R = Math.max(0, Math.min(255, R + Math.round(R * percent / 100)));
      G = Math.max(0, Math.min(255, G + Math.round(G * percent / 100)));
      B = Math.max(0, Math.min(255, B + Math.round(B * percent / 100)));

      const rr = R.toString(16).padStart(2, '0');
      const gg = G.toString(16).padStart(2, '0');
      const bb = B.toString(16).padStart(2, '0');

      return `#${rr}${gg}${bb}`;
    } catch (e) {
      console.error('색상 변환 오류:', e);
      return percent < 0 ? '#005bb9' : '#3399ff'; // 오류 시 기본 색상
    }
  }

  // 텍스트 색상 설정 (속성 패널에서 설정한 값 사용)
  const titleColor = componentProps?.titleColor || '#333';
  const labelColor = componentProps?.labelColor || '#333';
  const inputTextColor = componentProps?.inputTextColor || '#333';

  // 로컬 상태 관리
  const [formData, setFormData] = useState<LoginFormData>({
    user_id: "",
    password: ""
  });
  const [localValidationErrors, setLocalValidationErrors] = useState<LoginValidationErrors>({});

  // 리덕스의 validation 에러와 로컬 에러 병합
  const validationErrors = { ...localValidationErrors, ...reduxValidationErrors };

  // 컴포넌트 언마운트 시 로그인 상태 초기화
  useEffect(() => {
    return () => {
      dispatch(resetLoginState());
    };
  }, [dispatch]);

  // 프리뷰 모드 확인 - URL에 /preview가 포함되어 있는지
  const isPreviewMode = typeof window !== 'undefined' && 
    (window.location.pathname.includes('/preview') || 
     window.location.search.includes('preview'));

  // 로그인 성공 시 설정된 경로로 리다이렉트
  useEffect(() => {
    if (loginSuccess) {
      // 장바구니 정보 로드
      dispatch(fetchCartItems());
      
      const redirectTimer = setTimeout(() => {

        //모바일또는 태블릿이고, 24번 컴패니인 경우
        
        if ((withcookieData?.skin?.company?.id== 24 || withcookieData?.skin?.company?.id== 21 || withcookieData?.skin?.company?.id== 26) && (window.innerWidth < 900 || window.innerWidth < 1200)) {
          window.location.href = '/finapp';
        }else if ((withcookieData?.skin?.company?.id== 27 || withcookieData?.skin?.company?.id== 28) && (window.innerWidth < 900 || window.innerWidth < 1200)) {
          window.location.href = '/gpc';
        }
        else{
          window.location.href = finalRedirectUrl;
        }
      }, 1000); // 1초 후 리다이렉트 (성공 메시지를 잠시 보여주기 위함)

      
      return () => clearTimeout(redirectTimer);
    }
  }, [loginSuccess, dispatch, finalRedirectUrl,withcookieData]);

  // 입력값 변경 핸들러
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [id]: value
    }));

    // 유효성 검사
    validateField(id, value);
  };

  // 필드별 유효성 검사
  const validateField = (id: string, value: string) => {
    let errors = {...localValidationErrors};
    
    if (id === 'user_id') {
      if (!value.trim()) {
        errors.user_id = t('아이디를 입력해주세요');
      } else if (value.length < 2 || value.length > 20) {
        errors.user_id = t('아이디는 2~20자로 입력해주세요');
      } else {
        delete errors.user_id;
      }
    }
    
    if (id === 'password') {
      if (!value) {
        errors.password = t('비밀번호를 입력해주세요');
      } else {
        delete errors.password;
      }
    }
    
    setLocalValidationErrors(errors);
  };

  // 폼 전체 유효성 검사
  const validateForm = () => {
    const errors: LoginValidationErrors = {};
    const { user_id, password } = formData;
    
    if (!user_id.trim()) {
      errors.user_id = t('아이디를 입력해주세요');
    } else if (user_id.length < 2 || user_id.length > 20) {
      errors.user_id = t('아이디는 2~20자로 입력해주세요');
    }
    
    if (!password) {
      errors.password = t('비밀번호를 입력해주세요');
    }
    
    setLocalValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 폼 제출 핸들러
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // 유효성 검사
    if (!validateForm()) {
      return;
    }
    
    // Redux 액션 디스패치
    dispatch(loginUser(formData));
  };

  // 회원가입 링크 핸들러
  const handleSignupClick = () => {
    window.location.href = '/signup';
  };

  // 원본 스타일 정의 (인라인 스타일 방식 유지)
  const defaultStyle = {
    width: '100%', // 화면 너비 100%
    height: '100vh', // 화면 높이 100%
    padding: 0,
    margin: 0,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...style,
  } as React.CSSProperties;

  // 배경 타입에 따라 스타일 적용
  // 배경 제거 조건: 타입이 'none'이거나 URL이 비어있음
  const shouldRemoveBackground = backgroundType === 'none' || !backgroundUrl;
  
  if (shouldRemoveBackground) {
    // 배경 완전 제거
    defaultStyle.background = 'none';
    defaultStyle.backgroundImage = 'none';
    defaultStyle.backgroundColor = 'transparent';
    defaultStyle.backgroundSize = 'auto';
    defaultStyle.backgroundPosition = 'initial';
    defaultStyle.backgroundRepeat = 'initial';
    
  } else if (backgroundType === 'image' && backgroundUrl) {
    // 이미지 배경인 경우
    defaultStyle.backgroundImage = `url(${backgroundUrl})`;
    defaultStyle.backgroundSize = 'cover';
    defaultStyle.backgroundPosition = 'center';
    defaultStyle.backgroundRepeat = 'no-repeat';

  } else {
    // 그 외의 경우 - 배경 초기화
    defaultStyle.background = 'none';
    defaultStyle.backgroundImage = 'none';
    defaultStyle.backgroundColor = 'transparent';
  }
  // video 타입은 JSX에서 별도 처리

  // 배경 타입 변경 시 DOM 조작
  useEffect(() => {
    // 모든 강제 스타일 요소 제거
    const removeAllBackgroundStyles = () => {
      try {
        // ID로 생성된 모든 스타일 태그 제거
        const styleId = `no-bg-style-${props.id}`;
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
          existingStyle.remove();
        }
      } catch (e) {
        console.error('[LoginComponent] 스타일 제거 오류:', e);
      }
    };

    // 배경을 제거해야 하는 경우
    if (shouldRemoveBackground) {
      
      // 컴포넌트 ID로 DOM 요소 찾기
      setTimeout(() => {
        try {
          removeAllBackgroundStyles();
          
          const loginElement = document.querySelector(`[id^="component-"][id$="${props.id}"]`);
          if (loginElement) {
            
            // 클래스 추가
            loginElement.classList.add('force-no-background');
            loginElement.classList.add('no-background');
            
            // 인라인 스타일 적용
            const loginElementStyle = (loginElement as HTMLElement).style;
            loginElementStyle.background = 'none';
            loginElementStyle.backgroundImage = 'none';
            loginElementStyle.backgroundColor = 'transparent';
            
            // 비디오 요소 제거 (있을 경우)
            const videoElement = loginElement.querySelector('video');
            if (videoElement) {
              videoElement.remove();
            }
            
            // 오버레이 제거 (있을 경우)
            const overlayElement = loginElement.querySelector('.background-overlay');
            if (overlayElement) {
              overlayElement.remove();
            }

            // 데이터 속성 설정
            loginElement.setAttribute('data-background-type', 'none');
            
            // 전역 스타일 적용
            const styleElement = document.createElement('style');
            styleElement.id = `no-bg-style-${props.id}`;
            styleElement.textContent = `
              /* 강제 배경 제거 스타일 */
              [id^="component-"][id$="${props.id}"],
              [data-preview="true"] .login-form {
                background: none !important;
                background-image: none !important;
                background-color: transparent !important;
                background-size: auto !important;
                background-position: initial !important;
                background-repeat: initial !important;
              }
              
              /* 비디오 요소 강제 제거 */
              [id^="component-"][id$="${props.id}"] video,
              [data-preview="true"] .login-form video,
              [id^="component-"][id$="${props.id}"] .background-overlay,
              [data-preview="true"] .login-form .background-overlay {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
              }
            `;
            document.head.appendChild(styleElement);
          }
        } catch (e) {
          console.error('[LoginComponent] DOM 조작 오류:', e);
        }
      }, 100);
    } else if (backgroundType === 'video' && backgroundUrl) {
      // 컴포넌트 ID로 DOM 요소 찾기
      setTimeout(() => {
        try {
          // 이전에 적용된 배경 제거 스타일 제거
          removeAllBackgroundStyles();
          
          const loginElement = document.querySelector(`[id^="component-"][id$="${props.id}"]`);
          if (loginElement) {
            
            // 클래스 제거
            loginElement.classList.remove('force-no-background');
            loginElement.classList.remove('no-background');
            
            // 데이터 속성 설정
            loginElement.setAttribute('data-background-type', 'video');
            loginElement.setAttribute('data-has-background', 'true');
            
            // 인라인 스타일 적용
            const loginElementStyle = (loginElement as HTMLElement).style;
            loginElementStyle.backgroundImage = 'none';
            
            // 비디오 요소 갱신 - 기존 비디오 제거 후 새로 추가
            const existingVideo = loginElement.querySelector('video');
            if (existingVideo) {
              existingVideo.remove();
            }
            
            // 강제 리렌더링을 위해 renderCount 증가
            setRenderCount(prevCount => prevCount + 1);
          }
        } catch (e) {
          console.error('[LoginComponent] 비디오 업데이트 오류:', e);
        }
      }, 100);
    } else if (backgroundType === 'image' && backgroundUrl) {
      // 이미지 배경 업데이트
      setTimeout(() => {
        try {
          // 이전에 적용된 배경 제거 스타일 제거
          removeAllBackgroundStyles();
          
          const loginElement = document.querySelector(`[id^="component-"][id$="${props.id}"]`);
          if (loginElement) {
            
            // 클래스 제거
            loginElement.classList.remove('force-no-background');
            loginElement.classList.remove('no-background');
            
            // 데이터 속성 설정
            loginElement.setAttribute('data-background-type', 'image');
            loginElement.setAttribute('data-has-background', 'true');
            
            // 인라인 스타일 적용
            const loginElementStyle = (loginElement as HTMLElement).style;
            loginElementStyle.background = '';
            loginElementStyle.backgroundColor = '';
            loginElementStyle.backgroundImage = `url(${backgroundUrl})`;
            loginElementStyle.backgroundSize = 'cover';
            loginElementStyle.backgroundPosition = 'center';
            loginElementStyle.backgroundRepeat = 'no-repeat';
            
            // 비디오 요소 제거 (있을 경우)
            const videoElement = loginElement.querySelector('video');
            if (videoElement) {
              videoElement.remove();
            }
          }
        } catch (e) {
          console.error('[LoginComponent] 이미지 업데이트 오류:', e);
        }
      }, 100);
    }
  }, [backgroundType, backgroundUrl, props.id, shouldRemoveBackground]);

  // 폼 컨테이너 스타일 - 완전히 투명하게 처리
  const formContainerStyle = {
    backgroundColor: 'transparent', // 배경색을 투명하게 설정
    borderRadius: style.borderRadius || '8px',
    boxShadow: 'none', // 그림자 제거
    padding: style.padding || '30px',
    maxWidth: '500px',
    width: '90%',
    zIndex: 10,
    position: 'relative'
  } as React.CSSProperties;

  // 폼 스타일도 투명하게 수정
  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    width: '100%',
    backgroundColor: 'transparent' // 배경색 투명하게 설정
  } as React.CSSProperties;

  const formRowStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    width: '100%'
  } as React.CSSProperties;

  // 입력 필드 스타일 - 배경 약간 반투명하게 설정하여 가독성 유지
  const inputStyle = {
    padding: '10px 12px',
    border: '1px solid rgba(206, 212, 218, 0.7)',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
    transition: 'border-color 0.2s',
    color: inputTextColor,
    backgroundColor: 'rgba(255, 255, 255, 0.7)' // 약간 반투명한 배경
  };

  const errorInputStyle = {
    ...inputStyle,
    borderColor: 'rgba(220, 53, 69, 0.8)',
    backgroundColor: 'rgba(255, 248, 248, 0.8)'
  };

  const labelStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '6px',
    display: 'block',
    color: labelColor,
    textShadow: '0 0 4px rgba(255, 255, 255, 0.8)' // 가독성을 위한 텍스트 그림자
  } as React.CSSProperties;

  const buttonStyle = {
    padding: '12px 20px',
    backgroundColor: primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 'bold',
    marginTop: '10px',
    width: '100%',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)'
  };
  
  // 회원가입 버튼 스타일 - 로그인 버튼과 동일하게 변경
  const signupButtonStyle = {
    padding: '12px 20px',
    backgroundColor: primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    marginTop: '15px',
    width: '100%',
    textAlign: 'center',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    display: 'block',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)'
  } as React.CSSProperties;

  const errorMessageStyle = {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '4px',
    textShadow: '0 0 4px rgba(255, 255, 255, 0.8)'
  };

  const loginErrorStyle = {
    color: '#dc3545',
    fontSize: '14px',
    marginTop: '15px',
    padding: '12px',
    backgroundColor: 'rgba(255, 248, 248, 0.8)',
    borderRadius: '4px',
    borderLeft: '4px solid #dc3545'
  };

  const loginSuccessStyle = {
    textAlign: 'center',
    color: 'green',
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f8fff8',
    borderRadius: '4px'
  } as React.CSSProperties;

  const titleStyle = {
    marginTop: 0,
    marginBottom: '20px',
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
    color: titleColor
  } as React.CSSProperties;

  // 컴포넌트 제목 (content, data, componentProps 중 존재하는 값 사용)
  const title = content?.title || data?.title || componentProps?.title || t('로그인');

  // 비디오 배경 스타일
  const videoStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  } as React.CSSProperties;

  return (
    <div 
      className={`login-form ${className} ${shouldRemoveBackground ? 'no-background force-no-background' : ''}`} 
      style={defaultStyle} 
      data-background-type={backgroundType}
      data-has-background={!shouldRemoveBackground}
      data-render-count={renderCount}
      data-preview={isPreviewMode ? "true" : "false"}
      {...rest}
    >
      {/* 강제 스타일 적용 */}
      {backgroundType === 'none' && (
        <style dangerouslySetInnerHTML={{ __html: `
          /* 강제 배경 제거 스타일 */
          [id^="component-"][id$="${props.id}"],
          [data-preview="true"].login-form {
            background: none !important;
            background-image: none !important;
            background-color: transparent !important;
          }
          
          /* 비디오 요소 강제 제거 */
          [id^="component-"][id$="${props.id}"] video,
          [data-preview="true"].login-form video,
          [id^="component-"][id$="${props.id}"] .background-overlay,
          [data-preview="true"].login-form .background-overlay {
            display: none !important;
          }
        `}} />
      )}
      
      {/* 배경 타입별 렌더링 */}
      {backgroundType === 'video' && backgroundUrl && (
        <>
          <video
            style={videoStyle}
            autoPlay
            loop
            muted
            key={`video-${backgroundUrl}-${renderCount}`}
          >
            <source src={backgroundUrl} type="video/mp4" />
            {t('브라우저가 비디오 태그를 지원하지 않습니다.')}
          </video>
          <div 
            className="background-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              
              zIndex: 1
            }}
          ></div>
        </>
      )}
      
      {backgroundType === 'image' && backgroundUrl && (
        <div 
          className="background-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 1
          }}
        ></div>
      )}
      
      {/* 로그인 폼 */}
      <div style={formContainerStyle}>

        {(withcookieData?.skin?.company?.id !== 24 && withcookieData?.skin?.company?.id !== 26 && withcookieData?.skin?.company?.id !== 27) && (
          <h2 style={{...titleStyle, textShadow: '0 0 5px rgba(255, 255, 255, 0.8)'}}>{title}</h2>
        )}
        {(withcookieData?.skin?.company?.id == 24 || withcookieData?.skin?.company?.id == 26 || withcookieData?.skin?.company?.id == 27) && (
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <img src={withcookieData?.skin?.androidAppIconImage} alt="logo" style={{maxWidth:'300px', objectFit: 'contain', margin:'0 auto'}} />
          </div>
        )}


        
        {loginSuccess ? (
          <div style={{...loginSuccessStyle, backgroundColor: 'rgba(248, 255, 248, 0.8)'}}>
            <p>{t('로그인 성공! 환영합니다.')}</p>
          </div>
        ) : (
          <form style={formStyle} onSubmit={handleSubmit}>
            <div style={formRowStyle}>
              <label htmlFor="user_id" style={labelStyle}>{t('아이디')}</label>
              <input 
                id="user_id" 
                type="text" 
                placeholder={t("아이디 입력 (2~20자)")}
                required 
                style={validationErrors.user_id ? errorInputStyle : inputStyle}
                value={formData.user_id}
                onChange={handleChange}
                minLength={2}
                maxLength={20}
              />
              {validationErrors.user_id && (
                <div style={errorMessageStyle}>
                  {validationErrors.user_id}
                </div>
              )}
            </div>

            <div style={formRowStyle}>
              <label htmlFor="password" style={labelStyle}>{t('비밀번호')}</label>
              <input 
                id="password" 
                type="password" 
                placeholder={t("비밀번호 입력")}
                required 
                style={validationErrors.password ? errorInputStyle : inputStyle}
                value={formData.password}
                onChange={handleChange}
              />
              {validationErrors.password && (
                <div style={errorMessageStyle}>
                  {validationErrors.password}
                </div>
              )}
            </div>

            {loginError && (
              <div style={loginErrorStyle}>
                {typeof loginError === 'string' ? t(loginError) : JSON.stringify(loginError)}
              </div>
            )}

            <button 
              type="submit" 
              style={{
                ...buttonStyle,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
                backgroundColor: loading ? secondaryColor : primaryColor,
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
              disabled={loading}
              onMouseOver={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = primaryDarkColor;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.currentTarget.style.backgroundColor = primaryColor;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                }
              }}
            >
              {loading ? t('로그인 중...') : t('로그인')}
            </button>
            
            <a 
              href="/signup" 
              style={signupButtonStyle}
              onClick={(e) => {
                e.preventDefault();
                handleSignupClick();
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = primaryDarkColor;
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = primaryColor;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
            >
              {t('회원가입')}
            </a>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginComponent; 